import React, { useEffect, useState } from "react";
import api, { API } from "../lib/api";
import { toast } from "sonner";
import { Folder, FileText, ChevronRight, Upload, Trash2, Download, ArrowLeft } from "lucide-react";
import { useAuth } from "../lib/auth";
import { BRAND_ORANGE } from "../lib/brand";

const FOLDERS = {
  "patients/post-op": { label: "Post-Op Recommendations", parent: "patients" },
  "patients/consent": { label: "Consent Forms", parent: "patients" },
  "patients/financials": { label: "Financials", parent: "patients" },
  "admin/ce-center": { label: "CE Center", parent: "admin" },
};

const TOP_FOLDERS = [
  { id: "patients", label: "For Patients", subs: ["patients/post-op", "patients/consent", "patients/financials"] },
  { id: "admin", label: "Admin", subs: ["admin/ce-center"], adminOnly: true },
];

export default function DocCenter() {
  const { user } = useAuth();
  const [folder, setFolder] = useState(null); // null = root, "patients", "admin", or full folder path
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  const loadFiles = async (f) => {
    if (!f || !f.includes("/")) { setFiles([]); return; }
    const { data } = await api.get(`/doc-center/files?folder=${encodeURIComponent(f)}`);
    setFiles(data);
  };

  useEffect(() => { loadFiles(folder); }, [folder]);

  const uploadAndAttach = async (file, title) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await api.post("/admin/doc-center/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/admin/doc-center/files", {
        folder, title: title || file.name, description: "",
        image_path: up.path, original_filename: up.original_filename,
      });
      toast.success("File added");
      loadFiles(folder);
    } catch (e) {
      toast.error("Upload failed");
    } finally { setUploading(false); }
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const title = window.prompt("Title for this file:", f.name) || f.name;
    uploadAndAttach(f, title);
    e.target.value = "";
  };

  const removeFile = async (id) => {
    if (!window.confirm("Remove this file from the folder?")) return;
    await api.delete(`/admin/doc-center/files/${id}`);
    loadFiles(folder);
  };

  const downloadUrl = (f) => `${API}/files/${f.image_path}`;
  const download = async (f) => {
    try {
      const token = localStorage.getItem("token");
      const resp = await fetch(downloadUrl(f), { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error("Download failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = f.original_filename || f.title || "download";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  return (
    <div data-testid="doc-center-page" className="px-8 py-10 lg:px-14 lg:py-14 max-w-6xl">
      <div className="uppercase-label">Doc.Center</div>
      <h1 className="font-heading text-4xl font-semibold mt-2">Document Library</h1>
      <p className="text-muted-foreground mt-3 max-w-2xl">Practice documents organized by audience. Post-op recommendations, consent forms, financial info, and admin resources.</p>

      <Breadcrumbs folder={folder} onNav={setFolder} />

      <div className="mt-6" data-testid={`doc-view-${folder ? folder.replace("/", "-") : "root"}`}>
        {folder === null && <RootView onOpen={setFolder} isAdmin={user?.is_admin} />}
        {folder === "patients" && <SubfolderView parent="patients" onOpen={setFolder} onBack={() => setFolder(null)} />}
        {folder === "admin" && user?.is_admin && <SubfolderView parent="admin" onOpen={setFolder} onBack={() => setFolder(null)} />}
        {folder?.includes("/") && (
          <FileListView
            folder={folder}
            files={files}
            isAdmin={user?.is_admin}
            uploading={uploading}
            onUpload={handleFile}
            onRemove={removeFile}
            onDownload={download}
            onBack={() => setFolder(FOLDERS[folder]?.parent || null)}
          />
        )}
      </div>
    </div>
  );
}

function Breadcrumbs({ folder, onNav }) {
  const parts = [];
  parts.push({ id: null, label: "Doc.Center" });
  if (folder === "patients") parts.push({ id: "patients", label: "For Patients" });
  if (folder === "admin") parts.push({ id: "admin", label: "Admin" });
  if (folder?.includes("/")) {
    const [parent] = folder.split("/");
    parts.push({ id: parent, label: parent === "patients" ? "For Patients" : "Admin" });
    parts.push({ id: folder, label: FOLDERS[folder]?.label || folder });
  }
  return (
    <div data-testid="doc-breadcrumbs" className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
      {parts.map((p, i) => (
        <React.Fragment key={p.id || "root"}>
          {i > 0 && <ChevronRight size={14} />}
          <button data-testid={`doc-crumb-${p.id || "root"}`} onClick={() => onNav(p.id)} className={`${i === parts.length - 1 ? "text-ink font-medium" : "hover:text-ink"}`}>{p.label}</button>
        </React.Fragment>
      ))}
    </div>
  );
}

function RootView({ onOpen, isAdmin }) {
  const visible = TOP_FOLDERS.filter((f) => !f.adminOnly || isAdmin);
  return (
    <div data-testid="doc-root-grid" className="grid sm:grid-cols-2 gap-5">
      {visible.map((f) => (
        <button key={f.id} data-testid={`doc-folder-${f.id}`} onClick={() => onOpen(f.id)} className="module-card p-8 text-left hover:shadow-md transition-all">
          <div className="w-14 h-14 rounded-md flex items-center justify-center text-white" style={{ backgroundColor: BRAND_ORANGE }}>
            <Folder size={26} />
          </div>
          <div className="font-heading text-2xl font-semibold mt-5">{f.label}</div>
          <div className="text-sm text-muted-foreground mt-1">{f.subs.length} folder{f.subs.length === 1 ? "" : "s"}</div>
        </button>
      ))}
    </div>
  );
}

function SubfolderView({ parent, onOpen, onBack }) {
  const subs = TOP_FOLDERS.find(f => f.id === parent)?.subs || [];
  return (
    <div data-testid={`doc-subfolder-view-${parent}`}>
      <button onClick={onBack} data-testid="doc-back-button" className="text-sm text-muted-foreground hover:text-ink inline-flex items-center gap-1.5"><ArrowLeft size={14} /> Back</button>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-5">
        {subs.map((s) => (
          <button key={s} data-testid={`doc-subfolder-${s.replace('/','-')}`} onClick={() => onOpen(s)} className="module-card p-6 text-left hover:shadow-md transition-all">
            <div className="w-11 h-11 rounded-md flex items-center justify-center" style={{ backgroundColor: "#FDE6D8", color: BRAND_ORANGE }}>
              <Folder size={20} />
            </div>
            <div className="font-heading text-lg font-semibold mt-4">{FOLDERS[s]?.label}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function FileListView({ folder, files, isAdmin, uploading, onUpload, onRemove, onDownload, onBack }) {
  return (
    <div data-testid={`doc-file-list-${folder.replace("/", "-")}`}>
      <button onClick={onBack} data-testid="doc-back-button" className="text-sm text-muted-foreground hover:text-ink inline-flex items-center gap-1.5"><ArrowLeft size={14} /> Back</button>
      <div className="flex items-center justify-between mt-4">
        <div className="font-heading text-2xl font-semibold">{FOLDERS[folder]?.label}</div>
        {isAdmin && (
          <label className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-white font-medium text-sm cursor-pointer" style={{ backgroundColor: BRAND_ORANGE }}>
            <Upload size={15} /> {uploading ? "Uploading..." : "Upload file"}
            <input type="file" data-testid="doc-upload-input" className="hidden" onChange={onUpload} disabled={uploading} />
          </label>
        )}
      </div>
      <div className="mt-5 space-y-2">
        {files.length === 0 && <div className="text-sm text-muted-foreground p-5 border border-dashed border-gray-200 rounded-md text-center">No files yet. {isAdmin ? "Click Upload to add the first one." : "Check back soon."}</div>}
        {files.map((f) => (
          <div key={f.id} data-testid={`doc-file-${f.id}`} className="module-card p-4 bg-white flex items-center gap-3">
            <FileText size={18} className="text-muted-foreground shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{f.title}</div>
              <div className="text-xs text-muted-foreground truncate">{f.original_filename || "—"} · {new Date(f.created_at).toLocaleDateString()}</div>
            </div>
            <button data-testid={`doc-download-${f.id}`} onClick={() => onDownload(f)} className="p-2 hover:bg-bone rounded-md"><Download size={15} /></button>
            {isAdmin && <button data-testid={`doc-remove-${f.id}`} onClick={() => onRemove(f.id)} className="p-2 hover:bg-destructive/10 text-destructive rounded-md"><Trash2 size={15} /></button>}
          </div>
        ))}
      </div>
    </div>
  );
}
